import { Card, Row, Col, Typography, Tag, Space, Divider, Empty } from 'antd'
import { CheckCircleOutlined, CloseCircleOutlined, ArrowRightOutlined } from '@ant-design/icons'

const { Text } = Typography

/**
 * Deep equality check that handles JSON key order differences
 * Avoids false positives from JSON.stringify which is order-sensitive
 */
function deepEqual(a: unknown, b: unknown): boolean {
  // Handle null/undefined
  if (a === b) return true
  if (a == null || b == null) return false
  if (typeof a !== typeof b) return false

  // Handle arrays
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false
    return a.every((val, index) => deepEqual(val, b[index]))
  }

  // Handle objects
  if (typeof a === 'object' && typeof b === 'object') {
    const keysA = Object.keys(a).sort()
    const keysB = Object.keys(b).sort()
    if (keysA.length !== keysB.length) return false
    if (!keysA.every((key, index) => key === keysB[index])) return false
    return keysA.every((key) =>
      deepEqual((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key])
    )
  }

  // Primitive values
  return a === b
}

interface PreviewData {
  featureKey: string
  featureName: string
  currentState: {
    exists: boolean
    value: unknown
    enabled: boolean | null
  }
  proposedState: {
    value: unknown
    enabled: boolean
  }
  storagePath: string
}

interface FeaturePreviewProps {
  preview: PreviewData
}

/**
 * Format value for display - handle primitives and complex objects
 */
function formatValue(value: unknown): string {
  if (value === null || value === undefined) {
    return 'null'
  }
  if (typeof value === 'string') {
    return `"${value}"`
  }
  if (typeof value === 'boolean' || typeof value === 'number') {
    return String(value)
  }
  // For objects and arrays, format as JSON with indentation
  return JSON.stringify(value, null, 2)
}

export const FeaturePreview = ({ preview }: FeaturePreviewProps) => {
  const { currentState, proposedState, featureName, featureKey, storagePath } = preview

  const hasChanges = currentState.exists

  return (
    <Card title="Preview Changes" size="small">
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {/* Feature Info */}
        <div>
          <Text strong>{featureName}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Key: <code>{featureKey}</code>
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Storage Path: <code>{storagePath}</code>
          </Text>
        </div>

        <Divider style={{ margin: '8px 0' }} />

        {/* Status Indicator */}
        <div>
          {currentState.exists ? (
            <Tag color="blue" icon={<ArrowRightOutlined />}>
              Update Existing Feature
            </Tag>
          ) : (
            <Tag color="green" icon={<CheckCircleOutlined />}>
              New Feature Assignment
            </Tag>
          )}
        </div>

        {/* Side-by-side comparison */}
        <Row gutter={16}>
          {/* Current State */}
          <Col span={12}>
            <Card
              size="small"
              title={
                <Space>
                  <Text strong style={{ fontSize: 12 }}>
                    Current State
                  </Text>
                  {!currentState.exists && (
                    <Tag color="default" style={{ fontSize: 11 }}>
                      Not Assigned
                    </Tag>
                  )}
                </Space>
              }
              styles={{ body: { padding: '8px' } }}
            >
              {currentState.exists ? (
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  {/* Enabled Status */}
                  <div>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      Enabled:{' '}
                    </Text>
                    {currentState.enabled ? (
                      <Tag color="success" icon={<CheckCircleOutlined />} style={{ fontSize: 11 }}>
                        Yes
                      </Tag>
                    ) : (
                      <Tag color="error" icon={<CloseCircleOutlined />} style={{ fontSize: 11 }}>
                        No
                      </Tag>
                    )}
                  </div>

                  {/* Value */}
                  <div>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      Value:
                    </Text>
                    <pre
                      style={{
                        background: '#f5f5f5',
                        padding: '8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        margin: '4px 0 0 0',
                        overflow: 'auto',
                        maxHeight: '200px',
                      }}
                    >
                      {formatValue(currentState.value)}
                    </pre>
                  </div>
                </Space>
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No current value"
                  style={{ margin: '16px 0' }}
                />
              )}
            </Card>
          </Col>

          {/* Proposed State */}
          <Col span={12}>
            <Card
              size="small"
              title={
                <Space>
                  <Text strong style={{ fontSize: 12 }}>
                    Proposed State
                  </Text>
                  <Tag color="processing" style={{ fontSize: 11 }}>
                    New
                  </Tag>
                </Space>
              }
              styles={{ body: { padding: '8px' } }}
            >
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                {/* Enabled Status */}
                <div>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    Enabled:{' '}
                  </Text>
                  {proposedState.enabled ? (
                    <Tag color="success" icon={<CheckCircleOutlined />} style={{ fontSize: 11 }}>
                      Yes
                    </Tag>
                  ) : (
                    <Tag color="error" icon={<CloseCircleOutlined />} style={{ fontSize: 11 }}>
                      No
                    </Tag>
                  )}
                </div>

                {/* Value */}
                <div>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    Value:
                  </Text>
                  <pre
                    style={{
                      background: '#e6f7ff',
                      padding: '8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      margin: '4px 0 0 0',
                      overflow: 'auto',
                      maxHeight: '200px',
                      border: '1px solid #91d5ff',
                    }}
                  >
                    {formatValue(proposedState.value)}
                  </pre>
                </div>
              </Space>
            </Card>
          </Col>
        </Row>

        {/* Change Summary */}
        {hasChanges && (
          <>
            <Divider style={{ margin: '8px 0' }} />
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                <strong>Changes:</strong>
              </Text>
              <ul style={{ margin: '4px 0', paddingLeft: '20px', fontSize: '12px' }}>
                {currentState.enabled !== proposedState.enabled && (
                  <li>
                    Enabled status will change from{' '}
                    <Tag
                      color={currentState.enabled ? 'success' : 'error'}
                      style={{ fontSize: 11 }}
                    >
                      {currentState.enabled ? 'Yes' : 'No'}
                    </Tag>{' '}
                    to{' '}
                    <Tag
                      color={proposedState.enabled ? 'success' : 'error'}
                      style={{ fontSize: 11 }}
                    >
                      {proposedState.enabled ? 'Yes' : 'No'}
                    </Tag>
                  </li>
                )}
                {!deepEqual(currentState.value, proposedState.value) && (
                  <li>Value will be updated</li>
                )}
              </ul>
            </div>
          </>
        )}
      </Space>
    </Card>
  )
}
